<template>
  <section class="sign-page">
    <header class="head">
      <button type="button" class="back" @click="goBack">Back</button>
      <h2>Sign In</h2>
    </header>

    <div class="identity">
      <div class="row">
        <label>Invite Code</label>
        <input v-model.trim="userIdInput" type="text" placeholder="inviteCode" />
      </div>
      <div class="row">
        <label>Coin</label>
        <input v-model.trim="coinInput" type="text" placeholder="USDC / TRX" />
      </div>
      <button type="button" @click="applyManualIdentity">Apply Identity</button>
      <p class="msg">{{ message || "-" }}</p>
    </div>

    <div class="sign_wrap">
      <div class="sign_header">
        <div class="sign_day_num">
          <span>You have signed in for </span>
          <span class="day_font">{{ running_days }}</span>
          <span> days</span>
        </div>

        <button type="button" class="sign_btn" :class="{ over: isSign }" @click="signBtn">
          {{ isSign ? "Succeeded" : "Sign in" }}
        </button>
      </div>

      <div class="calendar_box">
        <div class="cd_tit">{{ title }}</div>
        <div class="calendar_header">
          <div class="block">Su</div>
          <div class="block">Mo</div>
          <div class="block">Tu</div>
          <div class="block">We</div>
          <div class="block">Th</div>
          <div class="block">Fr</div>
          <div class="block">Sa</div>
        </div>

        <div class="body">
          <div v-for="n in placeholderEleCount" :key="`place_${n}`" class="block" />
          <div v-for="(day, idx) in allDays" :key="`day_${idx}`" class="block">
            <span>{{ idx + 1 }}</span>
            <i :class="{ signs: day.sign }" />
          </div>
        </div>
      </div>

      <div v-if="dataList.length > 0" class="series_sign wrap_box">
        <p class="title">Cumulative sign in reward</p>
        <SignProcess v-if="id" class="process" :data="dataList" :id="numericId" :active="running_days" @init="init" />
      </div>

      <p class="tips">
        Note: bonus envelopes are directly distributed to the account balance.
      </p>

      <div v-if="signShow" class="sign_mask">
        <div class="sign_inner">
          <div class="award_tit">
            <p class="award_tips">Get Cash</p>
            <p class="award_num">{{ signMoney }} {{ currCoin }}</p>
          </div>

          <div class="sign_button btn_over">
            <span>Succeeded</span>
          </div>

          <button type="button" class="close_btn" @click="signShow = false">Close</button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import SignProcess from "../components/SignProcess.vue";
import { getUserInfo, getUserSign, getUserSignLog, getUserSignReward } from "../recovered/sign-api";

export default {
  name: "SignView",
  components: {
    SignProcess,
  },
  data() {
    return {
      id: "",
      c: "",
      loading: false,
      running_days: 0,
      title: "",
      isSign: false,
      placeholderEleCount: 0,
      allDays: [],
      rewardSetList: new Map(),
      signMoney: 1e-6,
      signShow: false,
      dataList: [],

      userIdInput: "",
      coinInput: "",
      message: "",
      currCoin: "",
    };
  },
  computed: {
    numericId() {
      const n = Number(this.id);
      return Number.isFinite(n) ? n : 0;
    },
  },
  mounted() {
    this.userIdInput = localStorage.getItem("sign_user_id") || "";
    this.coinInput = localStorage.getItem("sign_coin") || "";
    this.bootstrapUser();
  },
  methods: {
    goBack() {
      this.$router.back();
    },
    setMessage(msg) {
      this.message = msg || "";
    },
    currentServerDate() {
      const raw = localStorage.getItem("time");
      if (!raw) return new Date();
      const parsed = new Date(raw.replace(/-/g, "/"));
      return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
    },
    formatYMD(d) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}${m}${day}`;
    },
    createPlaceholders(year, month) {
      const firstDay = new Date(year, month - 1, 1).getDay();
      return Array.from({ length: firstDay }, (_, i) => i + 1);
    },
    async bootstrapUser() {
      try {
        const info = await getUserInfo();
        if (info && info.inviteCode) {
          this.id = String(info.inviteCode);
          this.c = info.coin || "USDC";
          this.currCoin = this.c;
          this.userIdInput = this.id;
          this.coinInput = this.c;
          localStorage.setItem("sign_user_id", this.id);
          localStorage.setItem("sign_coin", this.c);
          await this.init();
          this.setMessage("User info loaded from API");
          return;
        }
      } catch (err) {
        this.setMessage((err && err.message) || "GetUserInfo failed, using manual identity");
      }

      if (this.userIdInput && this.coinInput) {
        this.id = this.userIdInput;
        this.c = this.coinInput;
        this.currCoin = this.c;
        await this.init();
      }
    },
    async applyManualIdentity() {
      if (!this.userIdInput || !this.coinInput) {
        this.setMessage("Invite Code and Coin are required");
        return;
      }

      this.id = this.userIdInput;
      this.c = this.coinInput;
      this.currCoin = this.c;
      localStorage.setItem("sign_user_id", this.id);
      localStorage.setItem("sign_coin", this.c);

      try {
        await this.init();
        this.setMessage("Identity applied");
      } catch (err) {
        this.setMessage((err && err.message) || String(err));
      }
    },
    async signBtn() {
      if (this.isSign || !this.id || !this.c) return;

      this.loading = true;
      try {
        const amount = await getUserSign(this.id, this.c);
        if (amount !== undefined && amount !== null) {
          this.isSign = true;
          this.signShow = true;
          this.signMoney = amount;
          await this.init();
        }
      } catch (err) {
        this.setMessage((err && err.message) || String(err));
      } finally {
        this.loading = false;
      }
    },
    async init() {
      if (!this.id || !this.c) return;

      this.loading = true;
      const serverDate = this.currentServerDate();
      const ymd = this.formatYMD(serverDate);

      try {
        this.running_days = 0;

        const logs = await getUserSignLog(this.id);
        if (Array.isArray(logs)) {
          this.allDays = logs;
          this.isSign =
            logs.filter((x) => {
              if (x && x.sign) this.running_days += 1;
              return x && x.date === ymd && x.sign;
            }).length > 0;
        } else {
          this.allDays = [];
          this.isSign = false;
        }

        const rewardResp = await getUserSignReward(this.id, this.c);
        if (rewardResp && Array.isArray(rewardResp.config)) {
          const receivedMap = new Map();
          if (Array.isArray(rewardResp.list)) {
            rewardResp.list.forEach((x) => {
              receivedMap.set(x.dayNumber, x);
            });
          }

          this.rewardSetList = receivedMap;
          this.dataList = rewardResp.config.map((x) => {
            const current = { ...x };
            if (receivedMap.has(current.dayNumber)) {
              const fromMap = receivedMap.get(current.dayNumber);
              current.receiveId = fromMap.id;
              current.status = fromMap.status;
            } else {
              current.status = 0;
            }
            return current;
          });
        } else {
          this.dataList = [];
        }

        const year = serverDate.getFullYear();
        const month = serverDate.getMonth() + 1;
        this.title = `${year}-${month}`;
        this.placeholderEleCount = this.createPlaceholders(year, month);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.sign-page {
  max-width: 820px;
  margin: 0 auto;
}
.head {
  display: flex;
  align-items: center;
  gap: 12px;
}
.back {
  border: 1px solid #ccc;
  background: #fff;
  padding: 6px 10px;
}
.identity {
  margin: 12px 0;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
}
.identity .row {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
}
.identity input {
  flex: 1;
  min-width: 220px;
  padding: 6px 8px;
}
.msg {
  margin: 8px 0 0;
  color: #666;
  font-size: 13px;
}
.sign_wrap {
  margin-top: 20px;
}
.sign_header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 12px;
}
.day_font {
  font-weight: 700;
  margin: 0 6px;
}
.sign_btn {
  border: none;
  border-radius: 999px;
  background: #0dad51;
  color: #fff;
  padding: 8px 14px;
}
.sign_btn.over {
  background: #b4b4b4;
}
.calendar_box {
  margin-top: 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 12px;
}
.cd_tit {
  font-weight: 600;
  margin-bottom: 8px;
}
.calendar_header,
.body {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 6px;
}
.block {
  min-height: 40px;
  border-radius: 6px;
  border: 1px solid #eee;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}
.block i.signs {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #0dad51;
  position: absolute;
  right: 8px;
  bottom: 6px;
}
.wrap_box {
  margin-top: 12px;
}
.tips {
  margin-top: 10px;
  color: #666;
  font-size: 13px;
}
.sign_mask {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
}
.sign_inner {
  width: min(380px, 92vw);
  border-radius: 10px;
  background: #fff;
  padding: 16px;
}
.award_num {
  font-size: 24px;
  font-weight: 700;
}
.sign_button {
  margin: 12px 0;
  background: #0dad51;
  color: #fff;
  border-radius: 8px;
  padding: 8px;
  text-align: center;
}
.close_btn {
  border: 1px solid #ccc;
  background: #fff;
  width: 100%;
  padding: 8px;
  border-radius: 8px;
}
</style>
